import { ODOO_BASE_URL } from '../../odooConfig';

// Local development goes through the React dev server proxy (see setupProxy.js)
// to avoid browser CORS issues. Production calls Odoo directly.
const isLocalDevelopment =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

const BASE_URL = isLocalDevelopment ? '' : ODOO_BASE_URL;

// The Utilities portal uses the same Bearer token as the shared BU Dashboard login.
const TOKEN_KEYS = ['attendance_token', 'ea_token'];

export function getUtilitiesToken() {
  for (const key of TOKEN_KEYS) {
    try {
      const token = localStorage.getItem(key);
      if (token) return token;
    } catch {
      /* storage can be blocked; fall through */
    }
  }
  return null;
}

export class UtilitiesError extends Error {
  constructor(message, statusCode = null, errorType = null) {
    super(message || 'Request failed');
    this.name = 'UtilitiesError';
    this.statusCode = statusCode;
    this.errorType = errorType;
  }
}

/**
 * JSON-RPC 2.0 client for /api/utilities/*.
 * Failed calls throw UtilitiesError with the server's readable message.
 */
class UtilitiesAPI {
  async rpc(path, params = {}) {
    const token = getUtilitiesToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers.Authorization = `Bearer ${token}`;

    let response;
    try {
      response = await fetch(`${BASE_URL}${path}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ jsonrpc: '2.0', method: 'call', params }),
      });
    } catch {
      throw new UtilitiesError('Network error. Please check your connection.');
    }

    let body = null;
    try {
      body = await response.json();
    } catch {
      throw new UtilitiesError('The server returned an unexpected response.', response.status);
    }

    if (body?.error) {
      throw new UtilitiesError(body.error.message || 'Request failed', response.status);
    }

    const result = body?.result ?? body ?? {};
    if (result.success === false) {
      throw new UtilitiesError(result.message || 'Request failed', result.status_code, result.error);
    }
    return result;
  }

  // ---- Session / dashboard ----
  me() {
    return this.rpc('/api/utilities/me');
  }

  dashboard(companyId) {
    return this.rpc('/api/utilities/dashboard', { company_id: companyId });
  }

  // ---- Lookups (dropdown data) ----
  lookups(companyId, types) {
    const params = { company_id: companyId };
    if (types?.length) params.types = types;
    return this.rpc('/api/utilities/lookups', params);
  }

  outstandingBills(companyId, partnerId) {
    const params = { company_id: companyId };
    if (partnerId) params.partner_id = partnerId;
    return this.rpc('/api/utilities/lookups/outstanding-bills', params);
  }

  partnerBalance(companyId, partnerId) {
    return this.rpc('/api/utilities/lookups/partner-balance', {
      company_id: companyId,
      partner_id: partnerId,
    });
  }

  depositInvoices(companyId) {
    return this.rpc('/api/utilities/lookups/deposit-invoices', { company_id: companyId });
  }

  childBanks(companyId, invoiceId) {
    return this.rpc('/api/utilities/lookups/child-banks', {
      company_id: companyId,
      invoice_id: invoiceId,
    });
  }

  // ---- Forms (same endpoints for every resource) ----
  list(resource, companyId, filters = {}) {
    return this.rpc(`/api/utilities/${resource}/list`, { company_id: companyId, ...filters });
  }

  get(resource, companyId, id) {
    return this.rpc(`/api/utilities/${resource}/${id}`, { company_id: companyId });
  }

  create(resource, companyId, { values, lines, post = false }) {
    const params = { company_id: companyId, values };
    if (lines) params.lines = lines;
    if (post) params.post = true;
    return this.rpc(`/api/utilities/${resource}/create`, params);
  }

  update(resource, companyId, id, { values, lines }) {
    const params = { company_id: companyId, values };
    if (lines) params.lines = lines;
    return this.rpc(`/api/utilities/${resource}/${id}/update`, params);
  }

  post(resource, companyId, id) {
    return this.rpc(`/api/utilities/${resource}/${id}/post`, { company_id: companyId });
  }

  remove(resource, companyId, id) {
    return this.rpc(`/api/utilities/${resource}/${id}/delete`, { company_id: companyId });
  }
}

const utilitiesAPI = new UtilitiesAPI();
export default utilitiesAPI;
